import authService from './authService';
import {
  PurchaseOrder,
  PurchaseOrderFormData,
  PurchaseOrderSearchModel,
  PaymentMethod,
  PaymentMethodModel,
  ProviderProduct,
  PurchaseCreditCard,
} from '../types/purchaseOrder';
import type { ProviderPaymentTerm } from '../types/providerPaymentTerm';
import { ApiResponse } from '../types/common';
import { apiErrorFrom } from '../utils/apiError';
import { PurchaseOrderListPage, readLookupIds, readPurchaseOrderListPage } from './purchaseOrderResponse';

class PurchaseOrderService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ─── Payment Methods ────────────────────────────────────────────

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/payment-methods');
    if (!response.ok) throw new Error('Failed to fetch payment methods');
    const data: ApiResponse<PaymentMethod[]> = await response.json();
    return data.data!;
  }

  async getPaymentMethodModels(): Promise<PaymentMethodModel[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/payment-method-models');
    if (!response.ok) throw new Error('Failed to fetch payment method models');
    const data: ApiResponse<PaymentMethodModel[]> = await response.json();
    return data.data!;
  }

  /** Condição de pagamento padrão do fornecedor (null quando não há). */
  async getProviderPaymentTerm(providerId: number): Promise<ProviderPaymentTerm | null> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/purchase-orders/provider-payment-term/${providerId}`);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao carregar a condição de pagamento do fornecedor');
    const data: ApiResponse<ProviderPaymentTerm | null> = await response.json();
    return data.data ?? null;
  }

  /** Cartões das empresas (companyId ausente = todas) para a parcela paga com cartão. */
  async getPaymentCreditCards(companyId?: number): Promise<PurchaseCreditCard[]> {
    const apiClient = await this.getApiClient();
    const qs = companyId ? `?company_id=${companyId}` : '';
    const response = await apiClient.get(`/purchase-orders/credit-cards${qs}`);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao carregar os cartões');
    const data: ApiResponse<PurchaseCreditCard[]> = await response.json();
    return data.data ?? [];
  }

  // ─── Purchase Orders ───────────────────────────────────────────

  async getPurchaseOrders(searchModel: PurchaseOrderSearchModel): Promise<PurchaseOrderListPage & { success: boolean }> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { search: searchModel.searchName }),
      ...(searchModel.status !== undefined && { status: searchModel.status.toString() }),
      ...(searchModel.provider_id !== undefined && { provider_id: searchModel.provider_id.toString() }),
      ...(searchModel.company_id !== undefined && { company_id: searchModel.company_id.toString() }),
      ...(searchModel.is_active !== undefined && searchModel.is_active !== null && { is_active: searchModel.is_active.toString() }),
      ...(searchModel.is_priority !== undefined && searchModel.is_priority !== null && { is_priority: searchModel.is_priority.toString() }),
      ...(searchModel.date_from && { date_from: searchModel.date_from }),
      ...(searchModel.date_to && { date_to: searchModel.date_to }),
      ...(searchModel.orderBy && { orderBy: searchModel.orderBy }),
      ...(searchModel.orderDirection && { orderDirection: searchModel.orderDirection }),
      ...(searchModel.priority_first !== undefined && { priority_first: String(searchModel.priority_first) }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/purchase-orders?${params}`);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao carregar pedidos de compra');

    const json = await response.json();
    return { success: json.success, ...readPurchaseOrderListPage(json, searchModel.page, searchModel.pageSize) };
  }

  async getPurchaseOrderById(id: number): Promise<PurchaseOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/purchase-orders/${id}`);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao carregar pedido de compra');
    const data: ApiResponse<PurchaseOrder> = await response.json();
    return data.data!;
  }

  async createPurchaseOrder(formData: PurchaseOrderFormData): Promise<PurchaseOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/purchase-orders', formData);
    // O corpo do erro traz os campos (details) — "Failed to create purchase order"
    // escondia exatamente isso (parcela em branco, 10/09/2026).
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao criar pedido de compra');
    const data: ApiResponse<PurchaseOrder> = await response.json();
    return data.data!;
  }

  async updatePurchaseOrder(id: number, formData: Partial<PurchaseOrderFormData>): Promise<PurchaseOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/purchase-orders/${id}`, formData);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao atualizar pedido de compra');
    const data: ApiResponse<PurchaseOrder> = await response.json();
    return data.data!;
  }

  async deletePurchaseOrder(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/purchase-orders/${id}`);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao excluir pedido de compra');
  }

  /**
   * Buscar produtos de um fornecedor específico.
   * Retorna produtos com o preço de custo do fornecedor (provider_cost).
   * Endpoint: GET /api/providers/:providerId/purchase-products?q=query
   */
  async searchProductsByProvider(
    providerId: number,
    query?: string,
  ): Promise<ProviderProduct[]> {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/providers/${providerId}/purchase-products?${params}`);
    if (!response.ok) throw new Error('Failed to search products');
    const data: ApiResponse<ProviderProduct[]> = await response.json();
    return data.data ?? [];
  }

  // ─── PDF ──────────────────────────────────────────────────────

  async downloadPdf(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/purchase-orders/${id}/pdf`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar PDF (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `pedido-compra-${id}.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Lookup by order number (quick navigation) ───────────────

  /**
   * Busca os pedidos pelo número exibido na tela: o OrderNumber (Bling) ou, sem ele, o id.
   * Endpoint: GET /api/purchase-orders/lookup?order_number=<number>
   * Devolve todos os ids com aquele número (normalmente um). 404 se nenhum.
   */
  async lookupPurchaseOrder(orderNumber: string): Promise<number[]> {
    const apiClient = await this.getApiClient();
    const params = new URLSearchParams({ order_number: orderNumber.trim() });
    const response = await apiClient.get(`/purchase-orders/lookup?${params}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Pedido de compra não encontrado');
      if (response.status === 400) throw new Error('Digite só o número do pedido');
      throw new Error('Erro ao buscar pedido de compra');
    }
    const data: ApiResponse<{ purchase_order_id?: number; purchase_order_ids?: number[] }> = await response.json();
    return readLookupIds(data.data);
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }
}

// Singleton instance
export const purchaseOrderService = new PurchaseOrderService();
export default purchaseOrderService;
